import React, { useCallback, useContext, useMemo } from 'react'

import { getTranslate } from 'react-localize-redux'
import { connect } from 'react-redux'
import { isFunction, snakeCase } from 'lodash'
import { faPen, faRotateRight } from '@fortawesome/pro-solid-svg-icons'

import DataTable from '../antd/Tables/DataTable/DataTable'
import { getUser } from '../../reducers/UserReducer'
import { toFirstUpperCase } from '../../utils'
import { EUROPEAN_DATE, getTimezoneLessMoment, initEuropeanDate } from '../../utils/momentjs'
import { Action, DeleteAction } from '../antd/Tables/DataTable/utils/actions'
import { SchoolPeriodFormModalContext } from '../../Providers/Forms/SchoolPeriodForm/SchoolPeriodModalProvider'
import { SchoolPeriod } from '../../utils/entities/schoolPeriod'
import { HolidayActions } from './HolidayPeriodsManagement'

const mapStateToProps = state => ({ user: getUser(state.getUser), t: getTranslate(state.locale) })

const PREFIX = 'data_table.column_title.'
const COLUMNS_TITLES = ['startDate', 'endDate']

const HolidayPeriodsTable = ({
  data, loading, metadata, tableParameters, isReadOnly, user,
  onAction, onTableParametersChange, t
}) => {
  const { setFormHandler } = useContext(SchoolPeriodFormModalContext)

  const periods = useMemo(() => data.map(d => ({
    ...d,
    startDate: getTimezoneLessMoment(d.startDate).format(EUROPEAN_DATE),
    endDate: getTimezoneLessMoment(d.endDate).format(EUROPEAN_DATE)
  })), [data])

  const openModal = useCallback(({ id = -1, startDate, endDate } = {}) => {
    startDate = initEuropeanDate(startDate)
    endDate = initEuropeanDate(endDate)

    setFormHandler({
      data: new SchoolPeriod({ id, startDate, endDate, school: user.school.id }),
      callback: onAction
    })
  }, [user.school.id, onAction])

  const handleAction = useCallback(action => schoolPeriod => onAction(schoolPeriod, action), [onAction])

  const actions = useMemo(() => isReadOnly ? [] : [
    new Action({
      disabled: !isFunction(onAction),
      icon: faRotateRight,
      title: 'holiday_periods_table.actions.duplicate_period',
      onClick: handleAction(HolidayActions.DUPLICATE)
    }),
    new Action({
      disabled: !isFunction(onAction),
      icon: faPen,
      title: 'common.edit',
      onClick: openModal
    }),
    new DeleteAction({
      disabled: !isFunction(onAction),
      popconfirmTitle: 'holiday_periods_table.actions.delete_period.confirm',
      onDelete: handleAction(HolidayActions.DELETE)
    })
  ], [openModal, onAction])

  const formatColumns = useCallback((columns, translateKey) => columns.map(title => ({
    title: toFirstUpperCase(t(translateKey + snakeCase(title))),
    key: title,
    orderBy: title
  })), [t])

  const columns = useMemo(() => formatColumns(COLUMNS_TITLES, PREFIX), [formatColumns])

  return (
    <>
      <DataTable
        data={periods}
        columns={columns}
        actions={actions}
        loading={loading}
        parameters={tableParameters}
        metadata={metadata}
        addButtonLabel='holiday_periods_table.actions.add_period'
        onParametersChange={onTableParametersChange}
        onCreate={isReadOnly ? null : openModal}
      />
    </>
  )
}

export default connect(mapStateToProps)(HolidayPeriodsTable)
